import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  organizationsApi,
  healthFacilitiesApi,
  type FacilityOption,
  type CityOption,
} from '@/services/api';
import CreatableCombobox from '@/components/CreatableCombobox';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  XMarkIcon,
  TruckIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Coordinates are mandatory: they are the centre point the operating-zone
// check measures from, and an entity without them opts its whole fleet out of
// that check silently. Validated as numbers in range so a typo cannot place a
// hospital's centre in the ocean.
const coordField = (min: number, max: number, label: string) =>
  z.string()
    .min(1, `${label} مطلوب`)
    .refine((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= min && n <= max;
    }, `${label} يجب أن يكون رقماً بين ${min} و ${max}`);

const organizationSchema = z.object({
  // Optional: the facility directory this form draws on is Arabic-only, and
  // every screen falls back to the Arabic name. Requiring an English one only
  // produced invented transliterations.
  name: z.string().max(255).optional(),
  nameAr: z.string().min(1, 'الاسم بالعربية مطلوب'),
  type: z.enum(['ministry', 'hospital', 'department']),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('البريد الإلكتروني غير صالح').optional().or(z.literal('')),
  latitude: coordField(-90, 90, 'خط العرض'),
  longitude: coordField(-180, 180, 'خط الطول'),
  // Blank means "use the system default radius".
  geofenceRadiusKm: z.string().optional()
    .refine((v) => !v || (Number(v) > 0 && Number(v) <= 5000),
      'نصف القطر يجب أن يكون بين 0 و 5000 كم'),
  parentId: z.string().uuid().optional().nullable(),
  // Set when the entity was picked from the facility directory rather than
  // typed by hand; the server rejects a second import of the same facility.
  externalFacilityId: z.number().int().positive().optional().nullable(),
});

const DEFAULT_RADIUS_HINT = 20;

type OrganizationForm = z.infer<typeof organizationSchema>;

const typeLabels: Record<string, string> = {
  ministry: 'وزارة',
  hospital: 'مستشفى',
  department: 'قسم',
};

const typeColors: Record<string, string> = {
  ministry: 'badge-primary',
  hospital: 'badge-success',
  department: 'badge-warning',
};

export default function Organizations() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const queryClient = useQueryClient();

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['organizations', typeFilter],
    queryFn: async () => {
      const params: any = {};
      if (typeFilter) params.type = typeFilter;
      const response = await organizationsApi.getAll(params);
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: OrganizationForm) => organizationsApi.create({
      ...data,
      // A blank English name is absent, not an empty string.
      name: data.name?.trim() || undefined,
      email: data.email || undefined,
      parentId: data.parentId ?? undefined,
      externalFacilityId: data.externalFacilityId ?? undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('تم إنشاء الجهة بنجاح');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في إنشاء الجهة');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrganizationForm> }) =>
      organizationsApi.update(id, {
        ...data,
        name: data.name?.trim() || undefined,
        email: data.email || undefined,
        externalFacilityId: data.externalFacilityId ?? undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('تم تحديث الجهة بنجاح');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في تحديث الجهة');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => organizationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('تم حذف الجهة بنجاح');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في حذف الجهة');
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<OrganizationForm>({
    resolver: zodResolver(organizationSchema),
  });

  // ── Facility directory ────────────────────────────────────────────────
  // Facilities are filtered server-side: the upstream list is unpaginated and
  // can be large, so shipping all of it to the browser per modal open would be
  // a heavy payload for a dropdown. Cities are small enough to fetch once and
  // filter here, which avoids a round trip per keystroke.
  const [facilityQuery, setFacilityQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const debouncedFacilityQuery = useDebouncedValue(facilityQuery, 250);

  const { data: facilities = [], isFetching: facilitiesLoading } = useQuery<FacilityOption[]>({
    queryKey: ['health-facilities', debouncedFacilityQuery],
    queryFn: async () => (
      await healthFacilitiesApi.search({ q: debouncedFacilityQuery, limit: 30 })
    ).data.data ?? [],
    enabled: isModalOpen,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
    // A directory that is down must not be retried three times per keystroke;
    // the form still works by hand, which is the whole point of the fallback.
    retry: false,
  });

  const { data: cities = [], isFetching: citiesLoading } = useQuery<CityOption[]>({
    queryKey: ['health-facility-cities'],
    queryFn: async () => (await healthFacilitiesApi.cities()).data.data ?? [],
    enabled: isModalOpen,
    staleTime: 30 * 60_000,
    retry: false,
  });

  const visibleCities = cities.filter((c) => {
    const q = cityQuery.trim();
    return !q || c.name.includes(q);
  });

  /**
   * Filling the form from a directory row.
   *
   * Only ever called on an explicit selection, never while typing — free text
   * must not silently move an organization's centre point.
   */
  const applyFacility = (facility: FacilityOption) => {
    setValue('nameAr', facility.name, { shouldValidate: true, shouldDirty: true });
    setValue('externalFacilityId', facility.id, { shouldDirty: true });

    if (facility.latitude && facility.longitude) {
      setValue('latitude', facility.latitude, { shouldValidate: true, shouldDirty: true });
      setValue('longitude', facility.longitude, { shouldValidate: true, shouldDirty: true });
      // Overwriting coordinates during an edit is the intended meaning of
      // picking a different facility, but it must never happen silently.
      if (editingOrg) toast.success('تم تحديث الإحداثيات من دليل المرافق');
    } else {
      // Never write an empty string into a coordinate field: the server coerces
      // it to a valid 0 and the centre point lands in the Atlantic.
      toast('لا توجد إحداثيات لهذا المرفق في الدليل، الرجاء إدخالها يدوياً');
    }

    if (facility.city) {
      setValue('address', facility.city, { shouldValidate: true, shouldDirty: true });
      setCityQuery(facility.city);
    }

    // Suggested type only — and only when the operator has not already chosen
    // one. The directory's vocabulary is wider than our three values, so an
    // unmapped type leaves the field alone rather than guessing.
    if (facility.suggestedType && !dirtyFields.type) {
      setValue('type', facility.suggestedType, { shouldValidate: true });
    }
    setFacilityQuery(facility.name);
  };

  /**
   * Is an entity with this name already registered?
   *
   * A warning, never a block: two departments can legitimately share a name.
   * The hard guarantee is the unique index on the facility id, which turns a
   * genuine re-import into a 409.
   */
  const watchedNameAr = watch('nameAr');
  const duplicateOrg = !editingOrg && watchedNameAr?.trim()
    ? (organizations ?? []).find((o: any) =>
        (o.nameAr || '').trim() === watchedNameAr.trim())
    : undefined;

  /** The upstream type of the facility currently selected, for the type hint. */
  const selectedFacilityId = watch('externalFacilityId');
  const selectedFacilityType = facilities.find(
    (f) => f.id === (selectedFacilityId ?? -1),
  )?.type ?? null;

  const openModal = (org?: any) => {
    if (org) {
      setEditingOrg(org);
      reset({
        name: org.name || '',
        nameAr: org.nameAr,
        type: org.type,
        address: org.address || '',
        phone: org.phone || '',
        email: org.email || '',
        latitude: org.latitude || '',
        longitude: org.longitude || '',
        geofenceRadiusKm: org.geofenceRadiusKm ? String(org.geofenceRadiusKm) : '',
        parentId: org.parentId || null,
        externalFacilityId: org.externalFacilityId ?? null,
      });
      // Seed the pickers with what is stored, so an entity whose name has no
      // match in the directory still shows its own name rather than a blank.
      setFacilityQuery(org.nameAr || '');
      setCityQuery(org.address || '');
    } else {
      setEditingOrg(null);
      reset({
        name: '',
        nameAr: '',
        type: 'hospital',
        address: '',
        phone: '',
        email: '',
        latitude: '',
        longitude: '',
        geofenceRadiusKm: '',
        parentId: null,
        externalFacilityId: null,
      });
      setFacilityQuery('');
      setCityQuery('');
    }
    // The city list is fetched once and reused; starting it now lets the cold
    // fetch (which may walk several upstream pages) overlap the modal opening.
    queryClient.prefetchQuery({
      queryKey: ['health-facility-cities'],
      queryFn: async () => (await healthFacilitiesApi.cities()).data.data ?? [],
      staleTime: 30 * 60_000,
    }).catch(() => { /* the form works without it */ });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingOrg(null);
    setFacilityQuery('');
    setCityQuery('');
    reset();
  };

  const onSubmit = (data: OrganizationForm) => {
    if (editingOrg) {
      updateMutation.mutate({ id: editingOrg.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذه الجهة؟')) {
      deleteMutation.mutate(id);
    }
  };

  // `name` is optional now, so it can be null on rows imported from the
  // facility directory — this filter used to throw on the first such row.
  const filteredOrganizations = organizations?.filter((org: any) =>
    (org.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (org.nameAr || '').includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">الجهات</h1>
          <p className="text-gray-500 mt-1">إدارة المستشفيات والأقسام</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          إضافة جهة
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث عن جهة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pr-10"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">جميع الأنواع</option>
            {Object.entries(typeLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filteredOrganizations?.length === 0 ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <BuildingOffice2Icon className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium">لا توجد منظمات</p>
            <p className="text-sm">قم بإضافة جهة جديدة للبدء</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrganizations?.map((org: any) => (
            <div key={org.id} className="card hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                    <BuildingOffice2Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <span className={clsx('badge', typeColors[org.type])}>
                    {typeLabels[org.type]}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {org.nameAr}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{org.name}</p>

                <div className="space-y-2 text-sm text-gray-600">
                  {org.address && (
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{org.address}</span>
                    </div>
                  )}
                  {org.phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="w-4 h-4 text-gray-400" />
                      <span dir="ltr">{org.phone}</span>
                    </div>
                  )}
                  {org.email && (
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                      <span className="truncate" dir="ltr">{org.email}</span>
                    </div>
                  )}
                </div>

                {/* Zone monitoring only runs for entities with a centre point,
                    so its absence has to be visible, not silent. */}
                {org.latitude && org.longitude ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-success-700
                                  bg-success-50 border border-success-200 rounded-lg px-2.5 py-1.5">
                    <ShieldCheckIcon className="w-4 h-4 shrink-0" />
                    <span>
                      مراقبة النطاق مفعّلة — نصف القطر{' '}
                      <span className="font-semibold">
                        {org.geofenceRadiusKm ? Number(org.geofenceRadiusKm) : DEFAULT_RADIUS_HINT}
                      </span>{' '}كم
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-2 text-xs text-warning-800
                                  bg-warning-50 border border-warning-200 rounded-lg px-2.5 py-1.5">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                    <span>لا توجد إحداثيات — مراقبة النطاق معطّلة لمركبات هذه الجهة</span>
                  </div>
                )}

                {/* Fleet size, and a way straight into it. Counting is done
                    server-side; the list used to say nothing about how many
                    vehicles an entity actually operates. */}
                <div className="mt-3 flex items-center justify-between gap-2 rounded-lg
                                bg-gray-50 border border-gray-200 px-3 py-2">
                  <span className="text-sm text-gray-600 flex items-center gap-1.5">
                    <TruckIcon className="w-4 h-4 text-gray-400" />
                    {org.vehicleCount ?? 0} مركبة
                    {org.activeVehicleCount !== undefined
                      && org.activeVehicleCount !== org.vehicleCount
                      && <span className="text-xs text-gray-400">({org.activeVehicleCount} نشطة)</span>}
                  </span>
                  <Link
                    to={`/vehicles?organizationId=${org.id}`}
                    className="text-sm text-primary-600 hover:underline font-medium shrink-0"
                  >
                    عرض المركبات
                  </Link>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => openModal(org)}
                    className="btn-secondary btn-sm flex-1"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(org.id)}
                    className="btn-sm p-2 rounded-lg hover:bg-emergency-50 text-emergency-600"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4">
          {/* max-h + flex column so the body scrolls and the header and submit
              row stay reachable — the same shape the vehicles dialog uses. */}
          <Dialog.Panel className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[95dvh] sm:max-h-[90dvh] flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {editingOrg ? 'تعديل الجهة' : 'إضافة جهة جديدة'}
              </Dialog.Title>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="label">الاسم بالعربية *</label>
                {/* Searches the health-facility directory, but accepts anything
                    typed — a facility the directory has never heard of is a
                    normal case, not an error. */}
                <Controller
                  name="nameAr"
                  control={control}
                  render={({ field }) => (
                    <CreatableCombobox<FacilityOption>
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      onSelect={applyFacility}
                      options={facilities}
                      getKey={(f) => f.id}
                      getLabel={(f) => f.name}
                      renderMeta={(f) => [f.city, f.type].filter(Boolean).join(' • ')}
                      query={facilityQuery}
                      onQueryChange={setFacilityQuery}
                      isLoading={facilitiesLoading}
                      error={Boolean(errors.nameAr)}
                      placeholder="ابحث في دليل المرافق أو اكتب اسماً جديداً"
                      emptyMessage="لا يوجد مرفق بهذا الاسم في الدليل"
                    />
                  )}
                />
                {errors.nameAr ? (
                  <p className="error-message">{errors.nameAr.message}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    اختيار مرفق من الدليل يملأ الإحداثيات والمدينة تلقائياً
                  </p>
                )}
                {duplicateOrg && (
                  <div className="mt-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-warning-800">
                      جهة بهذا الاسم مسجّلة بالفعل.{' '}
                      <button
                        type="button"
                        className="underline font-medium"
                        onClick={() => { closeModal(); openModal(duplicateOrg); }}
                      >
                        فتحها للتعديل
                      </button>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="label">
                  الاسم بالإنجليزية <span className="text-gray-400 text-xs">(اختياري)</span>
                </label>
                <input
                  {...register('name')}
                  className={clsx('input', errors.name && 'input-error')}
                  placeholder="Tripoli Central Hospital"
                  dir="ltr"
                />
                {errors.name && (
                  <p className="error-message">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="label">النوع *</label>
                <select
                  {...register('type')}
                  className={clsx('input', errors.type && 'input-error')}
                >
                  {Object.entries(typeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                {errors.type && <p className="error-message">{errors.type.message}</p>}
                {selectedFacilityType && (
                  <p className="text-xs text-gray-500 mt-1">
                    نوع المرفق في الدليل: {selectedFacilityType}
                  </p>
                )}
              </div>

              <div>
                <label className="label">المدينة</label>
                {/* The directory's city list, searchable — and still free text,
                    so a city it does not list can simply be typed. */}
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <CreatableCombobox<CityOption>
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      options={visibleCities}
                      getKey={(c) => c.id ?? c.name}
                      getLabel={(c) => c.name}
                      renderMeta={(c) => (
                        c.facilityCount != null ? `${c.facilityCount} مرفق` : null
                      )}
                      query={cityQuery}
                      onQueryChange={setCityQuery}
                      isLoading={citiesLoading}
                      error={Boolean(errors.address)}
                      placeholder="اختر المدينة أو اكتبها"
                      emptyMessage="لا توجد مدينة بهذا الاسم في الدليل"
                    />
                  )}
                />
                {errors.address && (
                  <p className="error-message">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">رقم الهاتف</label>
                  <input
                    {...register('phone')}
                    className={clsx('input', errors.phone && 'input-error')}
                    placeholder="+218 21 1234567"
                    dir="ltr"
                  />
                  {errors.phone && (
                    <p className="error-message">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">البريد الإلكتروني</label>
                  <input
                    {...register('email')}
                    className={clsx('input', errors.email && 'input-error')}
                    placeholder="info@hospital.ly"
                    dir="ltr"
                  />
                  {errors.email && (
                    <p className="error-message">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Centre point + operating radius drive the zone alert. */}
              <div className="rounded-lg border border-primary-200 bg-primary-50/50 p-3 space-y-3">
                <div className="flex items-start gap-2">
                  <MapPinIcon className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">مركز الجهة ونطاق العمل</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      تُقاس المسافة من هذه النقطة. عند خروج مركبة عن نصف القطر يصدر تنبيه في لوحة
                      التحكم. بدون إحداثيات لا تعمل هذه المراقبة.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">خط العرض <span className="text-red-500">*</span></label>
                    <input
                      {...register('latitude')}
                      className={clsx('input', errors.latitude && 'input-error')}
                      placeholder="32.8872"
                      dir="ltr"
                      inputMode="decimal"
                    />
                    {errors.latitude && <p className="error-message">{errors.latitude.message}</p>}
                  </div>

                  <div>
                    <label className="label">خط الطول <span className="text-red-500">*</span></label>
                    <input
                      {...register('longitude')}
                      className={clsx('input', errors.longitude && 'input-error')}
                      placeholder="13.1913"
                      dir="ltr"
                      inputMode="decimal"
                    />
                    {errors.longitude && <p className="error-message">{errors.longitude.message}</p>}
                  </div>
                </div>

                <div>
                  <label className="label">نصف قطر نطاق العمل (كم)</label>
                  <input
                    {...register('geofenceRadiusKm')}
                    className={clsx('input', errors.geofenceRadiusKm && 'input-error')}
                    placeholder={`اتركه فارغاً لاستخدام الافتراضي (${DEFAULT_RADIUS_HINT} كم)`}
                    dir="ltr"
                    inputMode="decimal"
                  />
                  {errors.geofenceRadiusKm && (
                    <p className="error-message">{errors.geofenceRadiusKm.message}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!navigator.geolocation) {
                      toast.error('المتصفح لا يدعم تحديد الموقع');
                      return;
                    }
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setValue('latitude', pos.coords.latitude.toFixed(6), { shouldValidate: true });
                        setValue('longitude', pos.coords.longitude.toFixed(6), { shouldValidate: true });
                        toast.success('تم تعبئة الإحداثيات من موقعك الحالي');
                      },
                      () => toast.error('تعذّر تحديد الموقع الحالي'),
                    );
                  }}
                  className="btn btn-sm btn-outline w-full"
                >
                  <MapPinIcon className="w-4 h-4 ml-1" />
                  استخدام موقعي الحالي
                </button>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <div className="spinner w-5 h-5" />
                  ) : editingOrg ? (
                    'تحديث'
                  ) : (
                    'إضافة'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary flex-1"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
